import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CacheService } from '@core/cache/cache.service';
import { Category, CategoryNode } from '@core/category/category.types';
import { MerchantService } from '@core/merchant/merchant.service';
import { Merchant } from '@core/merchant/merchant.types';
import { Picture } from '@core/picture/picture.types';
import { EnvironmentService } from '@core/utils/environment/environment.service';
import { DEFAULT_HTTP_OPTIONS_NO_EXPIRE, HttpOptions } from '@core/utils/http-options.types';
import { environment } from '@env/environment';
import { cloneDeep } from 'lodash';
import { catchError, map, Observable, of, BehaviorSubject, switchMap, take, tap, throwError } from 'rxjs';

/**
 * Service for managing categories.
 */
@Injectable({
  providedIn: 'root'
})
export class CategoryService {

  /**
   * The current merchant.
   */
  private _merchant: Merchant;

  /**
   * The selected category.
   */
  private _category: BehaviorSubject<Category | null> = new BehaviorSubject(null);

  /**
   * The list of categories.
   */
  private _categories: BehaviorSubject<Category[]> = new BehaviorSubject(null);

  /**
   * The cache namespace.
   */
  private _cacheNamespace: string = 'category';

  /**
    * Subscribe to changes in the merchant and reload categories if the merchant changes.
    * @param _environmentService - The environment service.
    * @param _httpClient - The HTTP client.
    * @param _merchantService - The merchant service.
    * @param _cacheService - The cache service.
    */
  constructor(
    private _environmentService: EnvironmentService,
    private _httpClient: HttpClient,
    private _merchantService: MerchantService,
    private _cacheService: CacheService
  ) {
    // Get the merchant
    this._merchantService.merchant$
      .subscribe((merchant: Merchant) => {
        const reload: boolean = this._merchant?._id.toString() !== merchant?._id.toString();
        this._merchant = merchant;
        if (reload) {
          this._category.next(null);
          this._categories.next(null);
          this.getCategories()
            .subscribe();
        }
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Returns an observable of the categories.
   * @returns An observable of the categories.
   */
  get categories$(): Observable<Category[]> {
    return this._categories.asObservable();
  }

  /**
   * Returns an observable of the current category.
   * @returns An observable of the current category.
   */
  get category$(): Observable<Category> {
    return this._category.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Returns the SrcSet and sizes of the photos of the merchant.
   * @param {string} merchantName The name of the merchant.
   * @param {string[]} filenames The filenames of the photos.
   * @returns {{srcset: string, sizes: string}} The SrcSet and sizes of the photos.
   */
  private _getSrcSet(merchantName: string, filenames: string[]): {srcset: string, sizes: string} {
    const srcset: string[] = [];
    const breakpoints: any = {
      'xsmall': 100,
      'small': 320,
      'medium': 768,
      'large': 1024,
      'xlarge': 1440
    };
    for (const filename of filenames) {
      try {
        const width: number = parseInt(filename.split('-')[1]);
        const breakpoint: string = Object.keys(breakpoints).find((key) => breakpoints[key] === width);
        if (!breakpoint) {
          continue;
        }
        srcset.push(`${environment.qart.imagesUrl}/${merchantName}/${filename} ${width}w`);
      } catch (e) {
        continue;
      }
    }
    return {
      srcset: srcset.join(', ') || null,
      sizes: '(min-width: 1280px) 1440px, (min-width: 960px) 1024px, (min-width: 600px) 768px, 100vw'
    };
  };

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Returns a single Category object with the specified categoryId.
   * @param categoryId The ID of the category to retrieve.
   * @param options - The options related to cache and propagation.
   * @returns The requested category object.
   */
  getCategory(categoryId: string, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<Category> {
    // If there is no merchant, return null
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of(null);
    }
    // If the category is already loaded, return it
    const cacheParams: any = { merchantId: this._merchant._id, categoryId };
    if (options.cache && this._cacheService.has(this._cacheNamespace, 'category', cacheParams)) {
      const category: Category = this._cacheService.get(this._cacheNamespace, 'category', cacheParams);
      if (options.forcePropagate) {
        this._category.next(category);
      }
      return of(category);
    }
    // Otherwise, load the category from the array of categories previously loaded
    return this._categories.pipe(
      take(1),
      map((categories: Category[]) => {
        const category = categories.find(item => item._id === categoryId) || null;
        return category;
      }),
      switchMap((category: Category) => {
        if (!category) {
          console.error(`Error: Cannot find category with id of ${categoryId}!`);
          return of(null);
        }
        // Handle the logic of the cache and the propagation
        if (options.cache) {
          this._cacheService.set(this._cacheNamespace, 'category', cacheParams, category, options.expire);
        }
        if (options.propagate) {
          this._category.next(category);
        }
        return of(category);
      })
    );
  }

  /**
   * Returns an array of Category objects.
   * @param options - The options related to cache and propagation.
   * @returns An array of Category objects.
   */
  getCategories(options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<Category[]> {
    // If there is no merchant, return an empty array
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of([]);
    }
    // If the categories are already loaded, return them
    const cacheParams: any = { merchantId: this._merchant._id };
    if (options.cache && this._cacheService.has(this._cacheNamespace, 'categories', cacheParams)) {
      const categories: Category[] = this._cacheService.get(this._cacheNamespace, 'categories', cacheParams);
      if (options.forcePropagate) {
        this._categories.next(categories);
      }
      return of(categories);
    }
    // Otherwise, load the categories from the backend
    return this._httpClient.get<Category[]>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/categories`,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
    .pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(`Error: ${error}`);
        return of([]);
      }),
      // Drop the categories that are hidden
      map((categories: Category[]) => {
        return categories.filter(category => category.displayOnline);
      }),
      // Handle the logic of the cache and the propagation
      tap((categories: Category[]) => {
        if (options.cache) {
          this._cacheService.set(this._cacheNamespace, 'categories', cacheParams, categories, options.expire);
        }
        if (options.propagate) {
          this._categories.next(categories);
        }
      })
    );
  }

  /**
   * Find a category in a category tree.
   * @param categoryId - The ID of the category to find.
   * @param tree - The category tree to search.
   * @returns The category node if found, otherwise null.
   */
  findInTree(categoryId: string, tree: CategoryNode[]): CategoryNode {
    let result: CategoryNode;
    for (const node of tree) {
      if (node.id === categoryId) {
        return node;
      }
      if (node.children) {
        result = this.findInTree(categoryId, node.children);
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  /**
   * Build a category tree from a list of categories.
   * @param categories - The list of categories to build the tree from.
   * @returns The category tree.
   */
  buildCategoryTree(categories: Category[]): CategoryNode[] {
    if (!categories || categories.length === 0) {
      return [];
    }
    // Work with local copies
    const categoryList: Category[] = cloneDeep(categories);
    const categoryTree: CategoryNode[] = [];
    let prevLength = categoryList.length + 1;
    let curLength = categoryList.length;
    const processedCategoryIds: string[] = [];
    // Process all the root categories and add them to the tree
    while (curLength !== prevLength) {
      prevLength = categoryList.length;
      const categoryIndex = categoryList.findIndex(category => category.parentCategoryId == null || category.parentCategoryId === '');
      if (categoryIndex !== -1) {
        const category = categoryList.splice(categoryIndex, 1)[0];
        categoryTree.push({
          id: category._id.toString(),
          name: category.name,
          children: []
        });
        processedCategoryIds.push(category._id.toString());
      }
      curLength = categoryList.length;
    }
    // Find all the children categories
    prevLength = categoryList.length + 1;
    curLength = categoryList.length;
    while (curLength !== prevLength) {
      prevLength = categoryList.length;
      const categoryIndex = categoryList.findIndex(category => category.parentCategoryId != null && category.parentCategoryId !== '' && processedCategoryIds.includes(category.parentCategoryId));
      if (categoryIndex !== -1) {
        const node: CategoryNode = this.findInTree(categoryList[categoryIndex].parentCategoryId, categoryTree);
        if (node) {
          const category = categoryList.splice(categoryIndex, 1)[0];
          node.children.push({
            id: category._id.toString(),
            name: category.name,
            children: []
          });
          processedCategoryIds.push(category._id.toString());
        }
      }
      curLength = categoryList.length;
    }
    return categoryTree;
  }

  /**
   * Get the URLs of the photos of a category.
   * @param {Category} category - The category.
   * @returns {string[]} The URLs of the photos.
   */
  getPhotoUrls(category: Category): string[] {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return [];
    }
    let urls: string[] = [];
    if (category.photos && category.photos.length > 0) {
      urls = category.photos.map((photo) => {
        if (photo.source !== 'qart') {
          return photo.url;
        } else {
          const userPreferences: string = photo.useImage;
          const webpAvailable: boolean = photo.webpFilename && photo.webpFilename !== '';
          const aiModifiedAvailable: boolean = photo.aiModifiedFilename && photo.aiModifiedFilename !== '';
          if (userPreferences === 'original' || userPreferences === 'webp') {
            if (webpAvailable) {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.webpFilename}`;
            } else {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.filename}`;
            }
          } else if (userPreferences === 'aiModified') {
            if (aiModifiedAvailable) {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.aiModifiedFilename}`;
            } else if (webpAvailable) {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.webpFilename}`;
            } else {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.filename}`;
            }
          } else {
            return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.filename}`;
          }
        }
      });
    }
    return urls;
  }

  /**
   * Get the SrcSet URLs and sizes of the photos of the merchant.
   * @param {Category} category - The category.
   * @param {number} photoIdx The index of the photo.
   * @returns {{srcset: string, sizes: string}} The SrcSet URLs and sizes of the photos.
   */
  getPhotoSrcSet(category: Category, photoIdx: number): {srcset: string, sizes: string} {
    const response: {srcset: string, sizes: string} = {
      srcset: null,
      sizes: null
    };
    if (!this._merchant) {
      return response;
    }
    if (category.photos && category.photos.length < photoIdx) {
      return response;
    }
    const photo: Picture = category.photos[photoIdx];
    if (photo.source !== 'qart') {
      return response;
    }
    const userPreferences: string = photo.useImage;
    const srcSetWebpAvailable: boolean = photo.webpSrcSetFilenames && photo.webpSrcSetFilenames?.length > 0;
    const srcSetAiModifiedAvailable: boolean = photo.aiModifiedWebpSrcSetFilenames && photo.aiModifiedWebpSrcSetFilenames?.length > 0;
    if ((userPreferences === 'original' || userPreferences === 'webp') && srcSetWebpAvailable) {
      return this._getSrcSet(this._merchant.name, photo.webpSrcSetFilenames);
    }
    else if (userPreferences === 'aiModified') {
      if (srcSetAiModifiedAvailable) {
        return this._getSrcSet(this._merchant.name, photo.aiModifiedWebpSrcSetFilenames);
      } else if (srcSetWebpAvailable) {
        return this._getSrcSet(this._merchant.name, photo.webpSrcSetFilenames);
      }
    }
    return response;
  }

}
